import { Injectable } from '@angular/core'
import { SEARCH_MAX_LENGTH, SEARCH_MIN_LENGTH, SearchRequestType, SearchResponse } from '@ftr/contracts/api/search'
import { Uuid } from '@ftr/contracts/type/shared'
import { ApiResult, isNotNullOrUndefined } from '@ftr/foundation'
import { Store } from '@ngxs/store'
import { filter, switchMap } from 'rxjs'
import { SearchApiService } from '~app/services/search-api/search-api.service'
import { SearchState } from '~store/search/search.state'

export const SEARCH_PAGE_SIZE = 10

/**
 * Search service is just for API interaction
 */
@Injectable({
  providedIn: 'root',
})
export class SearchService {
  constructor(
    private readonly searchApiService: SearchApiService,
    private readonly store: Store,
  ) {}

  doSearch(
    query: string | undefined,
    searchType: SearchRequestType,
    pageNumber = 1,
    resourceId?: Uuid,
  ): ApiResult<SearchResponse> {
    /**
     * Search is currently meant to be scoped to the 'current court system' selected for a user. If this changes,
     * then the SearchResults <code>redirectForExactOrderMatches</code> will need to change as well.
     */
    return this.store
      .select(SearchState.searchScopeCourtSystem)
      .pipe(filter(isNotNullOrUndefined))
      .pipe(
        switchMap(courtSystem =>
          this.searchApiService.searchRequest({
            query: query as string,
            pageNumber: pageNumber ?? 1,
            resourceId,
            searchType,
            courtSystemId: courtSystem.id,
          }),
        ),
      )
  }
}

export function isValidSearchTerm(searchTerm: string | undefined): boolean {
  const term = searchTerm?.trim()
  return !!term && term.length >= SEARCH_MIN_LENGTH && term.length <= SEARCH_MAX_LENGTH
}

export function isValidSearchType(searchType: SearchRequestType): boolean {
  return Object.values(SearchRequestType).includes(searchType)
}

export function isValidSearchPage(searchPage: string): boolean {
  return !isNaN(parseInt(searchPage, 10)) && parseInt(searchPage, 10) > 0
}
